<script setup>
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useStore} from 'vuex'
import { debounce } from 'lodash'
import {formatTimeFromNowNumeric} from '@/utils/datetime'

import { MapSource, MapLayer, MapImageMarker, FeatureCollectionRenderer } from '@/components/map'
import { toast } from '@/components/ui/toast'
import { mergePolygons } from '@/utils/geometry'
import { setSelectedArea, clearSelectedArea } from '@/composables/useAreaSelection'
import { createLayerId } from '@/components/layers/types'

const props = defineProps({
  map: {
    type: Object,
    required: true,
  },
  project: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: String,
    required: true,
  },
})

const router = useRouter()
const route = useRoute()
const store = useStore()

const isLoggedIn = computed(() => store.getters.getIsLoggedIn)
const settings = computed(() => store.getters['user/getUserSettings'])
const beacons = computed(() => store.getters['geo/getBeacons'])
const params = computed(() => store.getters['search/getParams'])
const selectedRange = computed(() => store.state.layers.selectedRange)
const projectFilters = computed(() => store.state.geo.projectFilters)
const mapParamsKeys = ['zoom', 'center_lat', 'center_lng', 'south', 'north', 'east', 'west']

const features = computed(() => store.getters['geo/getVisibleFeatures'])

// Features filtered to exclude current page feature
const filteredFeatures = computed(() => {
  if (!features.value || !features.value.features) {
    return { type: 'FeatureCollection', features: [] }
  }

  return {
    type: 'FeatureCollection',
    features: features.value.features.filter(
      feature => feature.properties.code !== currentPageFeatureCode.value
    )
  }
})

const filterParams = computed(() => {
  return params.value.filter((param) => !mapParamsKeys.includes(param.key))
})

const routeProjectId = computed(() => parseInt(route.params.project_id))

const isCurrentProject = computed(() => {
  const routeProjectId = Number(route.params.project_id)
  return !isNaN(routeProjectId) && props.project.project_id === routeProjectId
})
const currentPageFeatureCode = computed(() => route.params.featureCode)

const loadInitialBeacons = async () => {
  if (!routeProjectId.value) return
  await store.dispatch('geo/fetchBeacons', {
    project_id: routeProjectId.value,
  })
}

let loadingTimer = null
let loadRequestId = 0

const loadData = async () => {
  if (!routeProjectId.value || !isLoggedIn.value || !isCurrentProject.value) return

  const currentRequestId = ++loadRequestId

  clearTimeout(loadingTimer)
  loadingTimer = setTimeout(() => {
    store.commit('SET_IS_LOADING_DATA', true)
  }, 10000)

  const paramsMap = params.value.reduce((acc, param) => {
    acc[param.key] = param.value
    return acc
  }, {})

  const filterParamsMap = filterParams.value.reduce((acc, param) => {
    acc[param.key] = param.value
    return acc
  }, {})

  // Use selectedRange from layers store (Navigator timeline) if available
  const [rangeStart, rangeEnd] = selectedRange.value || []

  const payload = {
    org_id: route.params.org_id,
    project_id: routeProjectId.value,
    shared: filterParamsMap['shared'],
    has_resources: filterParamsMap['has_resources'],
    startDate: rangeStart || filterParamsMap['start_date'],
    endDate: rangeEnd || filterParamsMap['end_date'],

    // map params
    south: paramsMap['south'],
    north: paramsMap['north'],
    east: paramsMap['east'],
    west: paramsMap['west'],
  }

  // Apply project filters (from ProjectFilters component)
  const filters = projectFilters.value
  if (filters) {
    if (filters.templateIds?.length > 0) {
      payload.template_ids = filters.templateIds.join(',')
    }
    if (filters.memberIds?.length > 0) {
      payload.member_ids = filters.memberIds.join(',')
    }
    if (filters.searchText?.trim()) {
      payload.search_text = filters.searchText
    }
    if (filters.isPublicData) {
      payload.is_public = true
    }
    if (filters.geometryTypes?.length > 0) {
      payload.geometry_types = filters.geometryTypes.join(',')
    }
    if (filters.geometries?.length > 0) {
      const merged = mergePolygons(filters.geometries)
      if (merged) {
        payload.geometry_filter = JSON.stringify(merged)
      }
    }
  }

  await store.dispatch('geo/fetchMapFeatures', payload)

  if (currentRequestId === loadRequestId) {
    clearTimeout(loadingTimer)
    store.commit('SET_IS_LOADING_DATA', false)
  }
}

const updateBounds = async () => {
  const bounds = props.map.getBounds()
  const center = props.map.getCenter()

  if (!bounds && !center) return

  const options = {
    center_lat: center.lat,
    center_lng: center.lng,
    zoom: props.map.getZoom(),
    south: bounds.getSouth(),
    north: bounds.getNorth(),
    east: bounds.getEast(),
    west: bounds.getWest()
  }

  for (const [key, value] of Object.entries(options)) {
    store.commit('search/ADD_PARAM', {
      key: key,
      value: value
    })
  }

  localStorage.setItem('mapOptions', JSON.stringify({
    center: props.map.getCenter(),
    zoom: props.map.getZoom()
  }))

  await loadData()
}

const debouncedUpdateBounds = debounce(updateBounds, 400)

// Area selection scopes the monitoring tabs to one polygon — routes opt in
// via meta.areaSelection instead of this component knowing their names.
const selectionMode = computed(() => Boolean(route.meta?.areaSelection))

const onFeatureClick = (feature) => {
  if (!selectionMode.value) return
  const type = feature.geometry?.type
  if (type !== 'Polygon' && type !== 'MultiPolygon') return
  setSelectedArea({
    projectId: props.project.project_id,
    code: feature.properties?.code,
    name: feature.properties?.name,
  })
}

// Both this handler and the layer click decide from the same hit test, so
// their firing order does not matter: a polygon hit selects, a miss deselects.
const onMapClick = (e) => {
  if (!selectionMode.value) return
  const fillId = createLayerId(`project-${props.project.project_id}`, 'fill')
  const hits = props.map.getLayer(fillId)
    ? props.map.queryRenderedFeatures(e.point, { layers: [fillId] })
    : []
  if (!hits.length) clearSelectedArea()
}

const setListeners = () => {
  props.map.on('dragend', debouncedUpdateBounds)
  props.map.on('zoomend', debouncedUpdateBounds)
  props.map.on('click', onMapClick)
}

const removeListeners = () => {
  props.map.off('dragend', debouncedUpdateBounds)
  props.map.off('zoomend', debouncedUpdateBounds)
  props.map.off('click', onMapClick)
}

onMounted(async () => {
  if (!isCurrentProject.value) return
  await loadInitialBeacons()
  await loadData()
  setListeners()
})

onUnmounted(() => {
  removeListeners()
  clearTimeout(loadingTimer)
})

watch(
  () => isCurrentProject.value, async (newValue, oldValue) => {
    if (newValue) {
      await loadData()
      setListeners()
    }

    if (!newValue) {
      removeListeners()
    }
  }
)

watch(
  () => filterParams.value,
  async (newValue, oldValue) => {
    if (!oldValue) return

    await loadData()
  },
  { deep: true, immediate: true }
)

// Watch timeline range changes to update map features
watch(selectedRange, async () => {
  if (isCurrentProject.value) {
    await loadData()
  }
}, { deep: true })

// Watch project filters to update map features
watch(projectFilters, async () => {
  if (isCurrentProject.value) {
    await loadData()
  }
}, { deep: true })
</script>

<template>
<div v-if="isCurrentProject">
  <!-- Beacons (real-time user locations) -->
  <div v-if="beacons.length" :key="beacons.length">
    <MapImageMarker
      v-for="beacon in beacons"
      :key="beacon.properties.code"
      ping
      :label="`${beacon.properties.member_display_name} (~${formatTimeFromNowNumeric(beacon.properties.rel_ts, settings.tz)})`"
      :url="beacon.properties.member_avatar_url"
      :feature="beacon"
      :map="map"
    />
  </div>

  <!-- Project features using unified renderer -->
  <FeatureCollectionRenderer
    v-show="filteredFeatures.features.length > 0"
    :map="map"
    :features="filteredFeatures"
    :group-id="`project-${project.project_id}`"
    :enable-clustering="true"
    :enable-image-markers="true"
    :enable-age-based-styling="true"
    @feature-click="onFeatureClick"
  />
</div>

</template>
